import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useWidgetContext } from 'widgets/WidgetContext';
import {
    useDispatchSetPortfolioSelection,
    usePortfolioActiveInputUsesOrdersStream,
} from 'portfolio/store/hooks';
import { OutliersToolbar } from 'widgets/outliers/components/OutliersToolbar';
import { OutliersContent } from 'widgets/outliers/components/OutliersContent';
import './Outliers.scss';
import { ZoomContextProvider } from 'utils/components/Zoom/ZoomContext';
import { scaleLinear } from 'd3-scale';
import { useOutliersData } from 'widgets/outliers/data/hooks';
import { outliersChartSeries } from 'widgets/outliers/components/chart/series/outliersChartSeries';
import { BubbleMouseOver } from 'widgets/outliers/components/chart/BubbleMouseOver';
import { Dimensions } from 'utils/types/Dimensions';
import { useOutliersConfigSlice } from 'widgets/outliers/store/config/configSlice';
import { useOutliersAxisDomains } from 'widgets/outliers/components/chart/axisStrategies';
import { buildPortfolioSelection } from 'portfolio/PortfolioSelection';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { useDispatchCurrentInputSelection } from 'portfolio/store/dispatcher';
import { useImpactModel } from 'impactModel/use-impact-model.ts';
import { ImpactModelWarning } from 'impactModel/ImpactModelWarning.tsx';
import {
    useImpactModelExceedsLimit,
    useImpactModelWarning,
} from 'impactModel/impact-model-slice.ts';

export function Outliers() {
    const { widgetKey, height, width } = useWidgetContext();

    const { register: registerImpactModel, deregister: deregisterImpactModel } =
        useImpactModel();

    const activeInputUsesOrdersStream =
        usePortfolioActiveInputUsesOrdersStream();

    const data = useOutliersData();
    const {
        config: { flipAxis },
    } = useOutliersConfigSlice();
    const bubbleMouseOverRef = useRef(new BubbleMouseOver());
    const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
    const [xBoundaries, setXBoundaries] = useState([0, 1]);
    const [yBoundaries, setYBoundaries] = useState([0, 1]);

    const xScaleRef = useRef(scaleLinear());
    const yScaleRef = useRef(scaleLinear());

    const { symbology } = useSelectAppSettings();
    const dispatchSetPortfolioSelection = useDispatchSetPortfolioSelection();
    const dispatchCurrentInputSelection = useDispatchCurrentInputSelection();

    const impactModelWarning = useImpactModelWarning();
    const impactModelExceedsLimit = useImpactModelExceedsLimit();

    const {
        config: { benchmarkType },
    } = useOutliersConfigSlice();

    const [plotAreaDimensions, setPlotAreaDimensions] = useState<Dimensions>({
        width: 1,
        height: 1,
    });

    useEffect(() => {
        if (benchmarkType === 'adjustedArrivalPrice') {
            registerImpactModel();
        } else {
            deregisterImpactModel();
        }
    }, [benchmarkType, deregisterImpactModel, registerImpactModel]);

    const onPlotAreaDimensionsChanged = useCallback(
        (nextDimensions: Dimensions) => {
            if (
                nextDimensions.height !== plotAreaDimensions.height ||
                nextDimensions.width !== plotAreaDimensions.width
            ) {
                setPlotAreaDimensions(nextDimensions);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const chartSeries = useMemo(
        () =>
            outliersChartSeries(
                flipAxis,
                plotAreaDimensions,
                data?.items ?? [],
                bubbleMouseOverRef.current,
                selectedOrderIds
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            flipAxis,
            data,
            bubbleMouseOverRef,
            selectedOrderIds,
            width,
            height,
            plotAreaDimensions,
        ]
    );

    const { xDomain, yDomain } = useOutliersAxisDomains(
        data,
        chartSeries,
        plotAreaDimensions
    );

    useEffect(() => {
        xScaleRef.current.domain(xDomain);
        xScaleRef.current.range([0, plotAreaDimensions.width]);
        setXBoundaries(xDomain);

        yScaleRef.current.domain(yDomain);
        yScaleRef.current.range([plotAreaDimensions.height, 0]);
        setYBoundaries(yDomain);
    }, [xDomain, yDomain, xScaleRef, yScaleRef, plotAreaDimensions]);

    const onSelectedOrderIdsChanged = (nextOrderIds: string[]) => {
        setSelectedOrderIds(nextOrderIds);
        const selectedOrders = (data?.items ?? []).filter(
            (d) => nextOrderIds.indexOf(d.orderID.toString()) > -1
        );
        if (selectedOrders.length === 0) {
            dispatchCurrentInputSelection();
        } else {
            dispatchSetPortfolioSelection(
                buildPortfolioSelection({
                    selectedOrders,
                    symbology,
                    overridingActiveInput: true,
                })
            );
        }
    };

    const doNotHaveRequiredImpactModels =
        (impactModelWarning || impactModelExceedsLimit) &&
        benchmarkType === 'adjustedArrivalPrice';
    const showContent =
        activeInputUsesOrdersStream && !doNotHaveRequiredImpactModels;

    return (
        <div className="Outliers" data-widget-id={widgetKey}>
            <ZoomContextProvider
                horizontal={{
                    boundaries: xBoundaries,
                    dataXScale: xScaleRef.current,
                    plotAreaWidth: plotAreaDimensions.width,
                }}
                vertical={{
                    boundaries: yBoundaries,
                    dataYScale: yScaleRef.current,
                    plotAreaHeight: plotAreaDimensions.height,
                }}
                resetZoomOnBoundaryChange={false}
            >
                <OutliersToolbar />
                {benchmarkType === 'adjustedArrivalPrice' && (
                    <ImpactModelWarning message="Adjusted Arrival prices may be inaccurate due to TradeSpex API error" />
                )}
                {showContent && (
                    <OutliersContent
                        selectedOrderIds={selectedOrderIds}
                        onSetSelectedOrderIds={onSelectedOrderIdsChanged}
                        chartSeries={chartSeries}
                        bubbleMouseOver={bubbleMouseOverRef.current}
                        onPlotAreaDimensionsChanged={
                            onPlotAreaDimensionsChanged
                        }
                    />
                )}
            </ZoomContextProvider>
        </div>
    );
}
